import React from 'react';
import { aboutContent } from '../../content/about-content';
import { WorkflowDiagram } from './infographics';
import NeuroInformedBadge from '../NeuroInformedBadge';

/**
 * Platform Features Section
 *
 * Shows the LRB platform workflow and key features.
 */

// Icons for platform features
const FeatureIcons = {
  flow: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  ),
  aggregate: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  ),
  match: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  )
};

const PlatformFeaturesSection = React.forwardRef(({ id = 'platform' }, ref) => {
  const { platformFeatures } = aboutContent;

  return (
    <section id={id} ref={ref} className="about-section">
      <header className="about-section__header">
        <h2 className="about-section__title">
          <svg className="about-section__title-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
            <line x1="8" y1="21" x2="16" y2="21" />
            <line x1="12" y1="17" x2="12" y2="21" />
          </svg>
          {platformFeatures.title}
        </h2>
        <p className="about-section__subtitle">{platformFeatures.intro}</p>
      </header>

      {/* Workflow Diagram */}
      <div style={{ marginBottom: '2.5rem' }}>
        <h3 style={{
          fontFamily: 'var(--lrbrain-font-mono)',
          fontSize: '0.9rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--lrbrain-navy)',
          marginBottom: '1rem'
        }}>
          {platformFeatures.workflow.title}
        </h3>
        <WorkflowDiagram steps={platformFeatures.workflow.steps} />
      </div>

      {/* Feature Cards */}
      <div className="info-cards">
        {platformFeatures.features.map((feature, index) => (
          <div key={index} className="info-card">
            <div className="info-card__icon">
              {FeatureIcons[feature.icon] || FeatureIcons.flow}
            </div>
            <h3 className="info-card__title">{feature.name}</h3>
            <p className="info-card__content">{feature.description}</p>
            <div style={{ marginTop: '1rem' }}>
              <NeuroInformedBadge
                technique={feature.technique}
                variant="subtle"
                size="small"
              />
            </div>
            <p className="info-card__example">
              <strong>Benefit:</strong> {feature.benefit}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
});

PlatformFeaturesSection.displayName = 'PlatformFeaturesSection';

export default PlatformFeaturesSection;
